import React, { useState, useEffect } from 'react';
import { compose } from 'redux-starter-kit';
import { connect } from 'react-redux';
import { Prompt, BoxWrapper, ButtonExpand } from 'components/Base';
import InvestmentFilter from './InvestmentFilter';
import OverviewTabContent from './OverviewTabContent';
import ChargesTabContent from './ChargesTabContent';
import PerformanceTabContent from './PerformanceTabContent';
import RiskTabContent from './RiskTabContent';
import BreakdownTabContent from './BreakdownTabContent';
import RatioTabContent from './RatioTabContent';
import { makeStyles } from '@material-ui/styles';
import CreateNewFolderIcon from '@material-ui/icons/CreateNewFolderOutlined';
import Store from 'store/store';
import api from 'api';
import Icons from 'components/Icons';
import { getCachableData } from './utils';
import { pastPerformancePalette as colors } from 'components/Charts/Utils';
import { get } from 'lodash';

const useStyles = makeStyles((theme: any) => {
  return {
    tab: {
      background: '#eeeeee',
      padding: '15px 25px',
      borderRadius: '42px',
      cursor: 'pointer',
      width: '100%',
      textAlign: 'center',
      fontWeight: 700
    },
    activeTab: {
      background: theme.palette.secondary.main,
      color: '#FFFFFF'
    }
  };
});

const { selectors, actions } = Store;

const Investment = ({
  selectedInvestment,
  investments,
  pastPerformance,
  pastPerformanceCacheIsValid,
  updatePastPerformance,
  cachedAssetAllocations,
  cachedSectorBreakdown,
  cachedCountryBreakdown,
  assetAllocationCacheIsValid,
  setCachedAssetAllocations,
  setCachedSectorBreakdown,
  setCachedCountryBreakdown,
  cachedRegionalBreakdown,
  setCachedRegionalBreakdown,
  riskRatings,
  riskRatingsCacheIsValid,
  updateRiskRatings,
  setScatterChartData,
  riskPerformancePeriod,
  backgroundChartsLoaded,
  updateResearchInvestments,
  imagesLoading,
  newAnalysis
}: any) => {
  const classes = useStyles();
  const [isOpen, setIsOpen] = useState(newAnalysis);
  const [activeTab, setActiveTab] = useState('Overview');
  const [funds, setFunds] = useState([] as any[]);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const [isLoading, setIsLoading] = useState(
    !pastPerformance.investments.length
  );
  const [isLoadingAllocation, setIsLoadingAllocation] = useState(
    !cachedAssetAllocations.investments.length
  );
  const [isLoadingSectorBreakdown, setIsLoadingSectorBreakdown] = useState(
    !cachedSectorBreakdown.investments.length
  );
  const [isLoadingCountryBreakdown, setIsLoadingCountryBreakdown] = useState(
    !cachedCountryBreakdown.investments.length
  );
  const [isLoadingRegionalBreakdown, setIsLoadingRegionalBreakdown] = useState(
    !cachedRegionalBreakdown.investments.length
  );
  const [isLoadingRisk, setIsLoadingRisk] = useState(
    !riskRatings.investments.length
  );

  useEffect(() => {
    backgroundChartsLoaded(
      !isLoading &&
        !isLoadingAllocation &&
        !isLoadingCountryBreakdown &&
        !isLoadingRegionalBreakdown &&
        !isLoadingSectorBreakdown &&
        !isLoadingRisk &&
        !imagesLoading.length
    );
  }, [
    isLoading,
    isLoadingAllocation,
    isLoadingCountryBreakdown,
    isLoadingRegionalBreakdown,
    isLoadingSectorBreakdown,
    isLoadingRisk,
    imagesLoading
  ]);

  const setScatter = () => {
    if (
      !pastPerformance.tabularData ||
      !Object.keys(pastPerformance.tabularData).length
    ) {
      return;
    }
    const number = (riskPerformancePeriod.match(/\d+/gm) || ['5'])[0] as string;

    setScatterChartData(
      investments.map((investment: any, index: number) => {
        const prefix = ({ '1': 'one', '3': 'three', '5': 'five' } as any)[
          number
        ];

        const figure = get(
          pastPerformance.tabularData[investment.name],
          `cumulative_${number}year_performance`,
          { value: null }
        );

        return {
          name: investment.name,
          color: colors[index],
          standard_deviation: investment[`${prefix}_y_standard_deviation`]
            ? investment[`${prefix}_y_standard_deviation`] * 100
            : null,
          performance: figure.value === null ? null : figure.value * 100
        };
      })
    );
  };

  const deleteResearchRelations = () => {
    api
      .deleteResearchRelations({
        research_item_id: selectedInvestment,
        delete_all: true
      })
      .then(() => {
        updateResearchInvestments([]);
        setIsDeleteModalOpen(false);
      });
  };

  useEffect(() => {
    if (activeTab === 'Performance') {
      return;
    }

    getCachableData(
      investments,
      pastPerformanceCacheIsValid,
      setIsLoading,
      () => api.getResearchItemPastPerformance({ id: selectedInvestment }),
      (response: any) =>
        updatePastPerformance({
          data: response.data.data,
          errors: response.data.failed_codes,
          tabularData: response.data.tabular_data,
          earliestDates: response.data.earliest_dates,
          footprints: response.data.footprints,
          investments
        })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pastPerformanceCacheIsValid]);

  useEffect(() => {
    getCachableData(
      investments,
      assetAllocationCacheIsValid,
      setIsLoadingAllocation,
      () => api.getResearchItemAssetAllocation(selectedInvestment),
      setCachedAssetAllocations
    );

    getCachableData(
      investments,
      assetAllocationCacheIsValid,
      setIsLoadingSectorBreakdown,
      () => api.getResearchItemSectorBreakdown(selectedInvestment),
      setCachedSectorBreakdown
    );

    getCachableData(
      investments,
      assetAllocationCacheIsValid,
      setIsLoadingCountryBreakdown,
      () => api.getResearchItemCountryBreakdown(selectedInvestment),
      setCachedCountryBreakdown
    );

    getCachableData(
      investments,
      assetAllocationCacheIsValid,
      setIsLoadingRegionalBreakdown,
      () => api.getResearchItemRegionalBreakdown(selectedInvestment),
      setCachedRegionalBreakdown
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetAllocationCacheIsValid]);

  useEffect(() => {
    setScatter();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [riskPerformancePeriod, pastPerformance.tabularData]);

  useEffect(() => {
    getCachableData(
      investments,
      riskRatingsCacheIsValid,
      setIsLoadingRisk,
      () => api.getResearchItemRiskRatings(selectedInvestment),
      updateRiskRatings,
      setScatter
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [riskRatingsCacheIsValid]);

  useEffect(() => {
    setFunds(investments.filter((investment: any) => investment.is_checked));
  }, [investments]);

  const handleTabClick = (tab: any) => {
    setActiveTab(tab);
  };

  const tabComponents: any = {
    Overview: OverviewTabContent,
    Charges: ChargesTabContent,
    Performance: PerformanceTabContent,
    Risk: RiskTabContent,
    Breakdown: BreakdownTabContent,
    Ratio: RatioTabContent
  };

  const TabComponent = tabComponents[activeTab];

  return (
    <>
      <BoxWrapper
        title="Investment Selection"
        headerChildren={
          <div
            style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}
          >
            <div
              className="cipThresholdDelete"
              onClick={() => setIsDeleteModalOpen(true)}
            >
              <Icons.DeleteIcon />
            </div>
            <ButtonExpand
              style={{
                height: '42px',
                marginRight: '1rem'
              }}
              isOpen={isOpen}
              onClick={() => setIsOpen(!isOpen)}
            >
              View
            </ButtonExpand>
          </div>
        }
        style={{ marginBottom: '2rem' }}
      >
        {isOpen && <InvestmentFilter selectedInvestment={selectedInvestment} />}
      </BoxWrapper>
      {funds.length === 0 ? (
        <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
          <CreateNewFolderIcon
            htmlColor="#6b7280"
            style={{ width: '36px', height: '36px' }}
          />
          <div style={{ fontWeight: 600 }}>No Investments Selected</div>
          <div style={{ color: '#6b7280' }}>
            Start building your Investment Analysis by selecting a new
            Investment Option above.
          </div>
        </div>
      ) : (
        <BoxWrapper style={{ marginTop: '2rem', marginBottom: '2rem' }}>
          <>
            <div style={{ padding: '1rem' }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-around',
                  gap: '15px'
                }}
              >
                {Object.keys(tabComponents).map(tab => (
                  <div
                    key={tab}
                    className={`${classes.tab} ${
                      activeTab === tab ? classes.activeTab : ''
                    }`}
                    onClick={() => handleTabClick(tab)}
                  >
                    {tab}
                  </div>
                ))}
              </div>
              <div>
                <TabComponent selectedInvestment={selectedInvestment} />
              </div>
            </div>
          </>
        </BoxWrapper>
      )}
      <Prompt
        isOpen={isDeleteModalOpen}
        title={`Are you sure you want to delete all the selections`}
        isProcessing={false}
        confirmAction={() => deleteResearchRelations()}
        cancelAction={() => setIsDeleteModalOpen(false)}
      />
    </>
  );
};

export default compose(
  connect(
    (state: any) => ({
      investments:
        selectors.investmentAnalyserSelectors.checkedResearchInvestments(state),
      pastPerformance:
        selectors.investmentAnalyserSelectors.pastPerformance(state),
      pastPerformanceCacheIsValid:
        selectors.investmentAnalyserSelectors.pastPerformanceCacheIsValid(
          state
        ),
      cachedAssetAllocations:
        selectors.investmentAnalyserSelectors.assetAllocation(state),
      cachedCountryBreakdown:
        selectors.investmentAnalyserSelectors.countryBreakdown(state),
      cachedRegionalBreakdown:
        selectors.investmentAnalyserSelectors.regionalBreakdown(state),
      cachedSectorBreakdown:
        selectors.investmentAnalyserSelectors.sectorBreakdown(state),
      assetAllocationCacheIsValid:
        selectors.investmentAnalyserSelectors.assetAllocationCacheIsValid(
          state
        ),
      riskRatings: selectors.investmentAnalyserSelectors.riskRatings(state),
      riskRatingsCacheIsValid:
        selectors.investmentAnalyserSelectors.riskRatingsCacheIsValid(state),
      riskPerformancePeriod:
        selectors.investmentAnalyserSelectors.riskPerformancePeriod(state),
      imagesLoading: selectors.reportSelectors.getImagesLoading(state)
    }),
    {
      updatePastPerformance:
        actions.investmentAnalyserActions.updatePastPerformance,
      setCachedAssetAllocations:
        actions.investmentAnalyserActions.updateAssetAllocation,
      setCachedCountryBreakdown:
        actions.investmentAnalyserActions.updateCountryBreakdown,
      setCachedRegionalBreakdown:
        actions.investmentAnalyserActions.updateRegionalBreakdown,
      setCachedSectorBreakdown:
        actions.investmentAnalyserActions.updateSectorBreakdown,
      updateRiskRatings: actions.investmentAnalyserActions.updateRiskRatings,
      setScatterChartData:
        actions.investmentAnalyserActions.updateScatterChartData,
      updateResearchInvestments:
        actions.investmentAnalyserActions.updateResearchInvestments
    }
  )
)(Investment);
